import { supabase } from '@/lib/supabase';

export interface TradingAccountDetails {
  id: string;
  number_account_trading: string;
  accountid_trading: string;
  balance_account: number;
  live: boolean;
  created_at: string;
  updated_at: string;
  last_balance_update: string | null;
  previous_balance: number | null;
  
  // API Connection info (secondary)
  api_connection: {
    id: string;
    api_name: string;
    client_id: string;
    status: string;
    refresh_status: string | null;
    balance_sync_status: string | null;
    expire_date: string | null;
    created_at: string;
    updated_at: string;
    
    // CSP Account info
    csp_account: {
      id: string;
      csp_account_name: string;
      csp_account_email: string;
      broker?: string;
      prop_firm?: string;
      status: string;
      user_id: string;
      
      // User info
      user: {
        id: string;
        email: string;
        name?: string;
      };
    };
  };
}

export interface TradingAccountsStats {
  total_accounts: number;
  live_accounts: number;
  demo_accounts: number;
  total_balance: number;
  active_connections: number;
  inactive_connections: number;
  expired_connections: number;
  recent_syncs: number;
  failed_syncs: number;
  unique_users: number;
  unique_brokers: number;
  average_balance: number;
}

export interface FilterOptions {
  status?: string;
  refresh_status?: string;
  balance_sync_status?: string;
  user_search?: string;
  broker?: string;
  prop_firm?: string;
  live_only?: boolean;
  date_from?: string;
  date_to?: string;
}

export const tradingAccountsService = {
  async getAccountsStats(): Promise<TradingAccountsStats> {
    // Get all trading accounts with connection data
    const { data: accounts, error: accountsError } = await supabase
      .from('trading_accounts')
      .select(`
        *,
        api_connections!inner(
          id,
          status,
          balance_sync_status,
          expire_date,
          last_balance_sync,
          csp_accounts!inner(
            broker,
            user_id
          )
        )
      `);

    if (accountsError) throw accountsError;

    const { data: connections, error: connectionsError } = await supabase
      .from('api_connections')
      .select('id, csp_account_id, api_name, status, client_id, expire_date, expires_in, last_refresh_attempt, last_balance_sync, balance_sync_status, balance_sync_error, refresh_status, created_at, updated_at');

    if (connectionsError) throw connectionsError;

    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Calculate unique values
    const uniqueUsers = new Set(accounts?.map(acc => (acc.api_connections as any).csp_accounts.user_id)).size;
    const uniqueBrokers = new Set(accounts?.map(acc => (acc.api_connections as any).csp_accounts.broker).filter(Boolean)).size;

    const totalBalance = accounts?.reduce((sum, acc) => sum + (acc.balance_account || 0), 0) || 0;
    const totalAccounts = accounts?.length || 0;

    const stats: TradingAccountsStats = {
      total_accounts: totalAccounts,
      live_accounts: accounts?.filter(acc => acc.live)?.length || 0,
      demo_accounts: accounts?.filter(acc => !acc.live)?.length || 0,
      total_balance: totalBalance,
      active_connections: connections?.filter(c => c.status === 'connected')?.length || 0,
      inactive_connections: connections?.filter(c => c.status !== 'connected')?.length || 0,
      expired_connections: connections?.filter(c => c.expire_date && new Date(c.expire_date) < now)?.length || 0,
      recent_syncs: connections?.filter(c => c.last_balance_sync && new Date(c.last_balance_sync) > oneDayAgo)?.length || 0,
      failed_syncs: connections?.filter(c => c.balance_sync_status === 'failed')?.length || 0,
      unique_users: uniqueUsers,
      unique_brokers: uniqueBrokers,
      average_balance: totalAccounts > 0 ? totalBalance / totalAccounts : 0,
    };

    return stats;
  },

  async getAllTradingAccounts(filters?: FilterOptions): Promise<TradingAccountDetails[]> {
    let query = supabase
      .from('trading_accounts')
      .select(`
        *,
        api_connections!inner(
          id,
          api_name,
          client_id,
          status,
          refresh_status,
          balance_sync_status,
          expire_date,
          created_at,
          updated_at,
          csp_accounts!inner(
            id,
            csp_account_name,
            csp_account_email,
            broker,
            prop_firm,
            status,
            user_id,
            users!inner(
              id,
              email,
              name
            )
          )
        )
      `)
      .order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    let filteredData = data || [];

    // Apply filters
    if (filters?.status) {
      filteredData = filteredData.filter(acc => 
        (acc.api_connections as any).status === filters.status
      );
    }
    
    if (filters?.refresh_status) {
      filteredData = filteredData.filter(acc => 
        (acc.api_connections as any).refresh_status === filters.refresh_status
      );
    }
    
    if (filters?.balance_sync_status) {
      filteredData = filteredData.filter(acc => 
        (acc.api_connections as any).balance_sync_status === filters.balance_sync_status
      );
    }

    if (filters?.user_search) {
      const searchTerm = filters.user_search.toLowerCase();
      filteredData = filteredData.filter(acc => {
        const connection = acc.api_connections as any;
        const cspAccount = connection.csp_accounts;
        const user = cspAccount.users;
        
        return cspAccount?.csp_account_name?.toLowerCase().includes(searchTerm) ||
               cspAccount?.csp_account_email?.toLowerCase().includes(searchTerm) ||
               user?.email?.toLowerCase().includes(searchTerm) ||
               user?.name?.toLowerCase().includes(searchTerm) ||
               acc.number_account_trading?.toLowerCase().includes(searchTerm) ||
               acc.accountid_trading?.toLowerCase().includes(searchTerm);
      });
    }

    if (filters?.broker) {
      filteredData = filteredData.filter(acc => 
        (acc.api_connections as any).csp_accounts?.broker === filters.broker
      );
    }

    if (filters?.prop_firm) {
      filteredData = filteredData.filter(acc => 
        (acc.api_connections as any).csp_accounts?.prop_firm === filters.prop_firm
      );
    }

    if (filters?.live_only !== undefined) {
      filteredData = filteredData.filter(acc => acc.live === filters.live_only);
    }
    
    if (filters?.date_from) {
      filteredData = filteredData.filter(acc => 
        new Date(acc.created_at) >= new Date(filters.date_from!)
      );
    }
    
    if (filters?.date_to) {
      filteredData = filteredData.filter(acc => 
        new Date(acc.created_at) <= new Date(filters.date_to!)
      );
    }

    // Transform the data to match the interface
    return filteredData.map(acc => ({
      id: acc.id,
      number_account_trading: acc.number_account_trading,
      accountid_trading: acc.accountid_trading,
      balance_account: acc.balance_account,
      live: acc.live,
      created_at: acc.created_at,
      updated_at: acc.updated_at,
      last_balance_update: acc.last_balance_update,
      previous_balance: acc.previous_balance,
      api_connection: {
        id: (acc.api_connections as any).id,
        api_name: (acc.api_connections as any).api_name,
        client_id: (acc.api_connections as any).client_id,
        status: (acc.api_connections as any).status,
        refresh_status: (acc.api_connections as any).refresh_status,
        balance_sync_status: (acc.api_connections as any).balance_sync_status,
        expire_date: (acc.api_connections as any).expire_date,
        created_at: (acc.api_connections as any).created_at,
        updated_at: (acc.api_connections as any).updated_at,
        csp_account: {
          id: (acc.api_connections as any).csp_accounts.id,
          csp_account_name: (acc.api_connections as any).csp_accounts.csp_account_name,
          csp_account_email: (acc.api_connections as any).csp_accounts.csp_account_email,
          broker: (acc.api_connections as any).csp_accounts.broker,
          prop_firm: (acc.api_connections as any).csp_accounts.prop_firm,
          status: (acc.api_connections as any).csp_accounts.status,
          user_id: (acc.api_connections as any).csp_accounts.user_id,
          user: {
            id: (acc.api_connections as any).csp_accounts.users.id,
            email: (acc.api_connections as any).csp_accounts.users.email,
            name: (acc.api_connections as any).csp_accounts.users.name,
          }
        }
      }
    }));
  },

  async syncAccountBalance(accountId: string): Promise<void> {
    // Get the account's connection ID
    const { data: account, error: fetchError } = await supabase
      .from('trading_accounts')
      .select('api_connection_id')
      .eq('id', accountId)
      .single();

    if (fetchError || !account) {
      throw new Error('Account not found');
    }

    const { error } = await supabase.functions.invoke('sync-ctrader-accounts', {
      body: { 
        connectionIds: [account.api_connection_id],
        syncAll: false 
      }
    });

    if (error) throw error;
  },

  async syncMultipleAccountBalances(accountIds: string[]): Promise<void> {
    // Get connection IDs for all accounts
    const { data: accounts, error: fetchError } = await supabase
      .from('trading_accounts')
      .select('api_connection_id')
      .in('id', accountIds);

    if (fetchError || !accounts) {
      throw new Error('Accounts not found');
    }

    const connectionIds = [...new Set(accounts.map(acc => acc.api_connection_id))];

    const { error } = await supabase.functions.invoke('sync-ctrader-accounts', {
      body: { 
        connectionIds,
        syncAll: false 
      }
    });

    if (error) throw error;
  },

  async refreshAccountConnection(accountId: string): Promise<void> {
    // Get the account's connection ID
    const { data: account, error: fetchError } = await supabase
      .from('trading_accounts')
      .select('api_connection_id')
      .eq('id', accountId)
      .single();

    if (fetchError || !account) {
      throw new Error('Account not found');
    }

    const { error } = await supabase.functions.invoke('refresh-ctrader-tokens', {
      body: { 
        connectionIds: [account.api_connection_id],
        refreshAll: false 
      }
    });

    if (error) throw error;
  },

  async refreshMultipleAccountConnections(accountIds: string[]): Promise<void> {
    // Get connection IDs for all accounts
    const { data: accounts, error: fetchError } = await supabase
      .from('trading_accounts')
      .select('api_connection_id')
      .in('id', accountIds);

    if (fetchError || !accounts) {
      throw new Error('Accounts not found');
    }

    const connectionIds = [...new Set(accounts.map(acc => acc.api_connection_id))];

    const { error } = await supabase.functions.invoke('refresh-ctrader-tokens', {
      body: { 
        connectionIds,
        refreshAll: false 
      }
    });

    if (error) throw error;
  },

  async disconnectAccount(accountId: string): Promise<void> {
    // Get the account's connection ID
    const { data: account, error: fetchError } = await supabase
      .from('trading_accounts')
      .select('api_connection_id')
      .eq('id', accountId)
      .single();

    if (fetchError || !account) {
      throw new Error('Account not found');
    }

    const { error } = await supabase
      .from('api_connections')
      .update({ status: 'disconnected' })
      .eq('id', account.api_connection_id);

    if (error) throw error;
  },

  async deleteAccount(accountId: string): Promise<void> {
    const { error } = await supabase
      .from('trading_accounts')
      .delete()
      .eq('id', accountId);

    if (error) throw error;
  },

  async bulkDisconnectApiConnections(connectionIds: string[]): Promise<any> {
    const { data, error } = await supabase.rpc('bulk_disconnect_api_connections', {
      connection_ids: connectionIds
    });
    
    if (error) throw error;
    return data;
  },

  async bulkDeleteApiConnections(connectionIds: string[]): Promise<any> {
    const { data, error } = await supabase.rpc('bulk_delete_api_connections_cascade', {
      connection_ids: connectionIds
    });
    
    if (error) throw error;
    return data;
  },

  async getUniqueFilters(): Promise<{
    brokers: string[];
    propFirms: string[];
    statuses: string[];
  }> {
    const { data, error } = await supabase
      .from('trading_accounts')
      .select(`
        api_connections!inner(
          status,
          csp_accounts!inner(
            broker,
            prop_firm
          )
        )
      `);

    if (error) throw error;

    const brokers = [...new Set(data?.map(item => 
      (item.api_connections as any)?.csp_accounts?.broker
    ).filter(Boolean))] as string[];
    
    const propFirms = [...new Set(data?.map(item => 
      (item.api_connections as any)?.csp_accounts?.prop_firm
    ).filter(Boolean))] as string[];
    
    const statuses = [...new Set(data?.map(item => 
      (item.api_connections as any)?.status
    ).filter(Boolean))] as string[];

    return { brokers, propFirms, statuses };
  }
};